import React, { useState } from 'react';
import { generatePlayerPageViewUrl, generateTypeOfContentView } from '@/utils/generatePlayerPageViewUrl';
import useVideoTime from '@/hook/useVideoTime';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useResize from '@/Pages/VideoPlayer/hooks/useResize';
import PlaylistHoverModalMobile from '@/v2/components/PlaylistHoverModalMobile';
import { Playlist } from '@/constants/Playlist';
import { Users } from '@/constants/Users';
import { ContentView } from '@/Pages/VideoPlayer/utils/queryParamsKeys';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { videoHasTags } from '@/utils/checkTags';
const VideoCardPlaylist = ({
	video,
	savedVideos = [],
	handleSaveUnsave,
	fld,
	handleDeleteVideo,
	viewType = null,
	subscriptionStatus = true,
	showSaveButton = true,
	selectedTags = [],
	selectTagCallback,
	isReordering
}) => {
	const navigate = useNavigate();
	const [isHovered, setIsHovered] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const userData = useAtomValue(userInfoAtom);
	const videoId = video?.videoDetails?._id || video?._id;
	const device_type = useResize();
	const isLiked =
		video?.videoDetails?.savedvideo || video?.savedvideo || (savedVideos?.length > 0 && savedVideos?.includes(videoId));

	const formattedDuration = video?.duration ? useVideoTime(video.duration) : '0:00';
	if (
		video?.tagsDetails?.some(tag => tag?.name?.includes('CLASS' || 'CLASSES')) ||
		video?.tags?.some(tag => tag?.name?.includes('CLASS' || 'CLASSES'))
	) {
		viewType = ContentView.CLASSES;
	}

	const getLink = () => {
		if (subscriptionStatus) {
			const typeOfContentView = generateTypeOfContentView(viewType);
			return generatePlayerPageViewUrl(videoId, typeOfContentView, fld?._id);
		}
		return `/plans`;
	};
	const handleSaveClick = e => {
		e.stopPropagation();
		handleSaveUnsave(videoId, video);
	};
	const handleCardClick = () => {
		navigate(getLink());
	};
	const canDeleteVideo = () => {
		return (
			(userData.token && (fld?.type === Playlist.PPL || fld?.type === Playlist.personalPlaylist)) ||
			(userData.user?.userType === Users.studioOwner &&
				(fld?.type === Playlist.studioPlaylist || fld?.type === Playlist.SPL))
		);
	};
	const handleDeleteClick = e => {
		e.stopPropagation();
		handleDeleteVideo(e, fld, video);
	};

	const handleMouseEnter = () => {
		if (device_type !== 'mobile') {
			setIsHovered(true);
		} else {
			setShowModal(true);
		}
	};

	const handleMouseLeave = () => {
		if (device_type !== 'mobile') {
			setIsHovered(false);
		}
	};

	const handleModalClose = () => {
		setShowModal(false);
	};
	const [longPressTimeout, setLongPressTimeout] = useState(null);

	const handleTouchStart = () => {
		if (device_type === 'mobile') {
			const timeout = setTimeout(() => {
				setShowModal(true);
			}, 600);
			setLongPressTimeout(timeout);
		}
	};

	const handleTouchEnd = () => {
		if (device_type === 'mobile' && longPressTimeout) {
			clearTimeout(longPressTimeout);
			setLongPressTimeout(null);
		}
	};

	const tagsPresent = videoHasTags(video);

	return (
		<>
			<div
				className="d-flex flex-row justify-content-between  align-items-center rounded-2 md:rounded-4 md:w-100 playlist-card"
				style={{ backgroundColor: '#27272A' }}
				onClick={() => handleCardClick()}
			>
				<div className="d-flex flex-row gap-1 gap-md-5 align-items-center md:w-100">
					{/* Thumbnail */}
					<div className="d-flex align-items-center gap-0 gap-lg-1  playlist-thumbnail" style={{ maxWidth: '200px' }}>
						{isReordering && <DragIndicatorIcon className="playlist_dot" style={{ color: 'white' }} />}
						<div
							onMouseEnter={device_type !== 'mobile' ? handleMouseEnter : undefined}
							onMouseLeave={device_type !== 'mobile' ? handleMouseLeave : undefined}
							onTouchStart={handleTouchStart}
							onTouchEnd={handleTouchEnd}
							style={{ position: 'relative', width: '100%' }}
						>
							<img
								src={video?.thumnailLink}
								className="img-fluid rounded"
								style={{
									objectFit: 'cover',
									width: '100%',
									maxHeight: '100px',
									borderRadius: '8px',
									pointerEvents: 'none'
								}}
							/>

							<div
								style={{
									position: 'absolute',
									bottom: '0',
									left: '0',
									width: '100%',
									fontSize: '0.75rem',
									display: 'flex',
									padding: '2px 4px',
									justifyContent: 'flex-start',
									gap: '0.6rem'
								}}
							>
								<span
									style={{
										background: 'black',
										padding: '4px',
										borderRadius: '4px',
										color: 'white'
									}}
								>
									{formattedDuration}
								</span>
							</div>
						</div>
					</div>

					{/* Title & Tags */}
					<div className="d-flex flex-column justify-content-start align-items-center">
						<h4 className="d-flex align-self-start text-white text-uppercase playlist-responsive-title">
							{video?.title}
						</h4>

						{tagsPresent && (
							<div className="d-flex flex-wrap gap-1 align-self-start">
								<Tags
									tags={video?.tagsDetails || video?.tagsData || video?.tags}
									selectedTags={selectedTags}
									selectTagCallback={selectTagCallback}
								/>
							</div>
						)}
					</div>
				</div>
				{/* Actions */}
				<div
					className={`flex-column gap-2 md:gap-3 mt-md-0 align-self-center 
        ${device_type === 'mobile' && isReordering ? 'd-none' : 'd-flex'}`}
				>
					{canDeleteVideo() && (
						<div onClick={handleDeleteClick} className="cursor-pointer">
							<DeleteOutlineIcon fontSize="small" className="text-white" />
						</div>
					)}

					{showSaveButton && userData.token && (
						<div onClick={handleSaveClick} className="cursor-pointer">
							{isLiked ? (
								<FavoriteIcon fontSize="small" className="text-danger" />
							) : (
								<FavoriteBorderIcon fontSize="small" className="text-white" />
							)}
						</div>
					)}
				</div>
			</div>
			{showModal && (
				<PlaylistHoverModalMobile
					selectedTags={selectedTags}
					formattedDuration={formattedDuration}
					video={video}
					showModal={showModal}
					selectTagCallback={selectTagCallback}
					onClose={handleModalClose}
					handlePlay={handleCardClick}
				/>
			)}
		</>
	);
};

export const Tags = ({ tags, selectedTags, selectTagCallback }) => {
	const { pathname } = useLocation();

	pathname.indexOf('Exercises'), pathname.indexOf('AcrosstheFloor'), pathname.indexOf('AllVideos');

	// Check if in library view
	let inLibraryView = true;
	if (
		pathname.indexOf('Lessons') === -1 &&
		pathname.indexOf('Exercises') === -1 &&
		pathname.indexOf('Acrossthefloor') === -1 &&
		pathname.indexOf('AllVideos') === -1
	) {
		inLibraryView = false;
	}

	if (!tags || tags.length === 0) return null;

	return (
		<div className="c-video_tag">
			<ul>
				{[...tags]
					.sort((a, b) => a?.priority - b?.priority)
					.map((tag, index) => (
						<li
							key={tag?._id || index}
							className="c-video_tag-entry-playlist"
							style={{
								background: tag?.color,
								borderColor: selectedTags?.includes(tag?._id) ? 'black' : 'transparent',
								color: selectedTags?.includes(tag?._id) ? 'black' : 'white'
							}}
							onClick={e => {
								e.stopPropagation();
								if (selectTagCallback) {
									selectTagCallback(tag);
								}
							}}
						>
							{!inLibraryView ? <Link to={`/AllVideos?tag=${tag?._id}`}>{tag?.name}</Link> : tag?.name}
						</li>
					))}
			</ul>
		</div>
	);
};

Tags.propTypes = {
	tags: PropTypes.arrayOf(
		PropTypes.shape({
			_id: PropTypes.string,
			name: PropTypes.string,
			color: PropTypes.string,
			priority: PropTypes.number
		})
	)
};

export default VideoCardPlaylist;
